import { Table } from 'antd';
import type { PaginationProps, TableProps } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { TablePaginationConfig } from 'antd/es/table';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';

const PAGINATION_DEFAULT_PROPS = {
  size: 'small',
  placement: ['bottomStart'],
} as const satisfies NonNullable<Exclude<TableProps['pagination'], false>>;

/**
 * Zebra striping keyed on the record index rather than a `:nth-child` rule, which an expanded row
 * would shift the parity of.
 */
export const getRowClassName = (_record: unknown, index: number) => (index % 2 ? 'even' : 'odd');

/**
 * antd fixes the rowClassName signature to (record, index), so "selectable" cannot be an option of
 * the function above — it has to be bound at the call site, which is what this second export is.
 * Rows that need it conditionally compose the two.
 */
export const getSelectableRowClassName = (record: unknown, index: number) =>
  `${getRowClassName(record, index)} selectable`;

const keepArrowsOnly: PaginationProps['itemRender'] = (_page, type, element) =>
  (type === 'prev' || type === 'next' ? element : null);

const sliceTotal = (totalElements: number, current: number, pageSize: number) =>
  (Number.isFinite(totalElements) ? totalElements : current * pageSize + 1);

const slicePagerStyle = css`
  && .ant-pagination-prev,
  && .ant-pagination-next {
    min-width: 22px;
    height: 22px;
    line-height: 20px;
    border-width: 0.5px;
    background-color: ${colors['@white']};
  }

  && .ant-pagination-disabled {
    opacity: 0.3;

    .ant-pagination-item-link {
      color: ${colors['@text-color']};
    }
  }

  .ant-pagination-prev {
    order: 0;
    margin-inline-end: 1px;
  }

  .ant-pagination-total-text {
    order: 1;
    margin-inline-end: 1px;
    padding: 0 8px;
    height: 22px;
    line-height: 22px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors['@text-color']};
  }

  .ant-pagination-next {
    order: 2;
    margin-inline-end: 16px;
  }

  && .ant-pagination-options {
    order: 3;
    margin-inline-start: 0;
  }

  && .ant-pagination-options .ant-select {
    height: 22px;
    --ant-select-height: 22px;
    --ant-select-border-size: 0.5px;
    --ant-select-font-size: 12px;
    --ant-select-color: ${colors['@text-color-secondary']};
    --ant-select-padding-horizontal: 8px;
    font-weight: 500;

    .ant-select-content {
      margin-inline-end: 8px;
    }

    .ant-select-suffix {
      font-size: 14px;
    }
  }
`;

type SlicePaginationConfig = TablePaginationConfig &
  Required<Pick<TablePaginationConfig, 'total' | 'itemRender' | 'showTotal' | 'onChange'>>;

type SlicePaginationOptions = {
  current: number;
  pageSize: number;
  totalElements: number;
  label?: React.ReactNode;
  disabled?: boolean;
  onChange: NonNullable<PaginationProps['onChange']>;
  onShowSizeChange: NonNullable<PaginationProps['onShowSizeChange']>;
};

export const slicePagination = ({
  current,
  pageSize,
  totalElements,
  label,
  disabled,
  onChange,
  onShowSizeChange,
}: SlicePaginationOptions): SlicePaginationConfig => ({
  current,
  pageSize,
  disabled,
  onChange: (page, size) => (size === pageSize ? onChange(page, size) : onShowSizeChange(page, size)),
  showSizeChanger: true,
  itemRender: keepArrowsOnly,
  className: slicePagerStyle,
  showTotal: () => label ?? <FormattedMessage id="pager" defaultMessage="Page {current}" values={{ current }} />,
  total: sliceTotal(totalElements, current, pageSize),
});

const FBTable = <RecordType extends AnyObject, >({ onRow, pagination, ...rest }: TableProps<RecordType>) => {
  const paginationProps = React.useMemo(
    () => (typeof pagination === 'object' ? { ...PAGINATION_DEFAULT_PROPS, ...pagination } : pagination),
    [pagination]
  );

  return (
    <Table<RecordType>
      {...rest}
      onRow={onRow}
      showSorterTooltip={false}
      pagination={paginationProps}
    />
  );
};

export default FBTable;
